/**
 * /projects/:projectId/plans — read-only design-package viewer (Workspace.1).
 *
 * Crew working a task need the floorplan and furniture schedule at their
 * fingertips. This renders the project's design-package PDF in the browser
 * (react-pdf / pdfjs, same as the punch-pinning canvas) with labeled quick-jumps
 * to the plan / 3D / schedule pages. Read-only — no pin editing here.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import pdfWorkerSrc from 'pdfjs-dist/build/pdf.worker.mjs?url';
import { ArrowLeft, ChevronLeft, ChevronRight, Download, Loader2 } from 'lucide-react';
import type { DesignPagePart } from '@vari-crew/schemas';
import { useDesignPackage } from '@/api/design-package';
import { attachmentFileUrl } from '@/api/attachments';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';

// One-time pdfjs worker registration (idempotent — the canvas sets it too).
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;

const PART_LABEL: Record<DesignPagePart, string> = {
  cover: 'Cover',
  as_built_furniture_plan: 'As-built plan',
  as_built_3d_view: 'As-built 3D',
  as_built_furniture_schedule: 'As-built schedule',
  proposed_furniture_plan: 'Proposed plan',
  proposed_3d_view: 'Proposed 3D',
  proposed_furniture_schedule: 'Proposed schedule',
  general_notes: 'Notes',
  other: 'Page',
};

const PLAN_PARTS: DesignPagePart[] = ['as_built_furniture_plan', 'proposed_furniture_plan'];

export function PlansViewer() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { data: pkg, isLoading, error, refetch } = useDesignPackage(projectId);

  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(0); // 0-based
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    function measure() {
      if (containerRef.current) setWidth(containerRef.current.clientWidth);
    }
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [pkg?.id]);

  // pdfjs fetches without auth headers — the signed file_url is required now
  // that the bare route needs credentials. Deps stay on the primitive id so a
  // refetch's fresh signature doesn't reload the whole PDF.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- sig churn must not reload the PDF
  const fileUrl = useMemo(() => (pkg ? attachmentFileUrl(pkg) : ''), [pkg?.id]);
  const pages = useMemo(() => pkg?.pages ?? [], [pkg]);

  // Open on the furniture plan when the package first loads.
  useEffect(() => {
    if (!pages.length) return;
    const plan = pages.find((p) => PLAN_PARTS.includes(p.part));
    setPage(plan ? plan.page_index : pages[0]!.page_index);
  }, [pkg?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const lastPage = (numPages || pages.length || 1) - 1;

  return (
    <div className="min-h-full bg-fill-soft pb-10">
      <div className="max-w-3xl mx-auto px-4 pt-4 space-y-3">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-ink-dim hover:text-ink"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <header className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold leading-tight">Plans &amp; docs</h1>
            {pkg && (
              <p className="text-xs text-ink-dim mt-0.5 truncate">{pkg.original_filename}</p>
            )}
          </div>
          {pkg && fileUrl && (
            <a
              href={fileUrl}
              download={pkg.original_filename ?? 'plans.pdf'}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-line px-2.5 py-1.5 text-xs font-medium text-ink-dim hover:text-ink hover:bg-fill-soft"
            >
              <Download className="w-3.5 h-3.5" /> Download
            </a>
          )}
        </header>

        {isLoading ? (
          <div className="grid place-items-center py-16">
            <Loader2 className="w-5 h-5 animate-spin text-ink-faint" />
          </div>
        ) : error ? (
          // Distinct from "no plans": crew in the field must know whether the
          // drawings don't exist or just didn't load.
          <div
            className="bg-bg rounded-2xl border border-line p-8 text-center text-sm space-y-3"
            role="alert"
          >
            <div className="text-status-blocked-fg">
              Couldn&apos;t load the plans ({error.message}).
            </div>
            <button
              onClick={() => refetch()}
              className="inline-flex items-center rounded-md border border-line px-3 py-1.5 text-xs font-medium text-ink-dim hover:text-ink hover:bg-fill-soft"
            >
              Try again
            </button>
          </div>
        ) : !pkg ? (
          <div className="bg-bg rounded-2xl border border-line p-8 text-center text-sm text-ink-dim">
            No plans uploaded for this project yet.
          </div>
        ) : (
          <>
            {/* Labeled quick-jumps to the plan / 3D / schedule pages. */}
            {pages.length > 0 && (
              <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
                {pages.map((p) => (
                  <button
                    key={p.page_index}
                    onClick={() => setPage(p.page_index)}
                    className={cn(
                      'whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                      p.page_index === page
                        ? 'border-accent bg-accent text-accent-fg'
                        : 'border-line bg-bg text-ink-dim hover:bg-fill-soft',
                    )}
                  >
                    {PART_LABEL[p.part]}
                    {p.page_label ? ` · ${p.page_label}` : ''}
                  </button>
                ))}
              </div>
            )}

            <div
              ref={containerRef}
              className="bg-bg rounded-2xl border border-line overflow-hidden grid place-items-center min-h-[50vh]"
            >
              {width > 0 && fileUrl && (
                <Document
                  file={fileUrl}
                  onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                  loading={
                    <div className="grid place-items-center py-16">
                      <Loader2 className="w-5 h-5 animate-spin text-ink-faint" />
                    </div>
                  }
                  error={
                    <div className="p-8 text-center text-sm text-status-blocked-fg">
                      Couldn’t load the plans PDF.
                    </div>
                  }
                >
                  <Page
                    pageNumber={page + 1}
                    width={width}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                  />
                </Document>
              )}
            </div>

            {/* Page stepper. */}
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page <= 0}
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </Button>
              <span className="text-xs text-ink-dim tabular-nums">
                Page {page + 1} of {numPages || pages.length || '…'}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                disabled={page >= lastPage}
              >
                Next <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
